import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
import { createParser, useQueryState } from "nuqs";

import { getS3DownloadUrl } from "@/api/s3/s3.mutations";
import type {
  ApprovalCallbackPayload,
  ApprovalTarget,
  BreadcrumbEntry,
  CreateFolderCallbackPayload,
  DeleteCallbackPayload,
  DeleteTarget,
  FileManagerContainerProps,
  FileManagerItem,
  FileManagerOperationCallbacks,
  FileManagerQueryContext,
  LockedOperation,
  MoveCallbackPayload,
  MoveOrCopyTarget,
  RenameCallbackPayload,
  RenameTarget,
  TagsTarget,
  UpdateNoteCallbackPayload,
  UpdateTagsCallbackPayload,
  UploadFileItem,
} from "./FileManager.types.ts";
import { FileManagerContext } from "./FileManagerContext/FileManagerContext.ts";
import { FileManagerUIContext } from "./FileManagerContext/FileManagerUIContext.ts";

const breadcrumbParser = createParser<BreadcrumbEntry[]>({
  parse: (value) => {
    if (!value) return [];
    return value.split("|").map((segment) => {
      const idx = segment.indexOf("~");
      if (idx === -1) return { id: segment, name: segment };
      const name = segment
        .slice(idx + 1)
        .replace(/%7E/gi, "~")
        .replace(/%7C/gi, "|");
      return { id: segment.slice(0, idx), name };
    });
  },
  serialize: (entries) =>
    entries.map((e) => `${e.id}~${e.name.replace(/~/g, "%7E").replace(/\|/g, "%7C")}`).join("|"),
}).withDefault([]);

export function FileManagerContainer({
  fetchItems,
  onUpload,
  onDeleteFiles,
  onRename,
  onCreateFolder,
  onMoveItems,
  onCopyItems,
  onUpdateTags,
  onUpdateNote,
  onApprove,
  onGetShareLink,
  onResolveDownloadUrl,
  permissions = {},
  filterItems: filterItemsFn,
  lockedFolders = [],
  treePeekDuration = 0,
  queryScope = [],
  urlState = true,
  children,
}: FileManagerContainerProps) {
  const [urlBreadcrumb, setUrlBreadcrumb] = useQueryState("path", breadcrumbParser);
  const [localBreadcrumb, setLocalBreadcrumb] = useState<BreadcrumbEntry[]>([]);
  const breadcrumb = urlState ? urlBreadcrumb : localBreadcrumb;
  const setBreadcrumb = urlState ? setUrlBreadcrumb : setLocalBreadcrumb;
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(
    () => breadcrumb.at(-1)?.id ?? null,
  );

  const [urlSearch, setUrlSearch] = useQueryState("search");
  const [localSearch, setLocalSearch] = useState<string | null>(null);
  const search = urlState ? urlSearch : localSearch;
  const setSearch = urlState ? setUrlSearch : setLocalSearch;
  const [innerSearch, setInnerSearch] = useState(search ?? "");
  const [debouncedSearch] = useDebouncedValue(innerSearch, 350);

  const effectiveSearch = filterItemsFn ? null : debouncedSearch || null;

  const queryCtx: FileManagerQueryContext = {
    currentFolderId,
    breadcrumb,
    search: effectiveSearch,
  };

  const queryClient = useQueryClient();

  const {
    data: rawItems = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["fileManager", ...queryScope, currentFolderId, effectiveSearch] as const,
    queryFn: () => fetchItems({ currentFolderId, breadcrumb, search: effectiveSearch }),
  });

  const filterItemsRef = useRef(filterItemsFn);
  filterItemsRef.current = filterItemsFn;

  const items = useMemo(
    () =>
      filterItemsRef.current && debouncedSearch
        ? filterItemsRef.current(debouncedSearch, rawItems)
        : rawItems,
    [debouncedSearch, rawItems],
  );
  const folders = useMemo(() => items.filter((i) => i.type === "FOLDER"), [items]);
  const files = useMemo(() => items.filter((i) => i.type === "FILE"), [items]);

  const onSearch = (term: string) => {
    void setSearch(term);
    setInnerSearch(term);
  };

  const onClearSearch = () => {
    void setSearch(null);
    setInnerSearch("");
  };

  const openFolder = (id: string | null, name?: string, path?: BreadcrumbEntry[]) => {
    setIsSelectionMode(false);
    setSelectedMap({});
    onClearSearch();
    if (!id) {
      setCurrentFolderId(null);
      void setBreadcrumb([]);
      return;
    }
    const next =
      path ??
      (() => {
        const idx = breadcrumb.findIndex((b) => b.id === id);
        return idx !== -1
          ? breadcrumb.slice(0, idx + 1)
          : [...breadcrumb, { id, name: name ?? id }];
      })();
    setCurrentFolderId(id);
    void setBreadcrumb(next);
  };

  const goBack = () => {
    setIsSelectionMode(false);
    setSelectedMap({});
    const next = breadcrumb.slice(0, -1);
    setCurrentFolderId(next.at(-1)?.id ?? null);
    void setBreadcrumb(next);
  };

  const navigateToBreadcrumb = (index: number) => {
    setIsSelectionMode(false);
    setSelectedMap({});
    const next = breadcrumb.slice(0, index + 1);
    setCurrentFolderId(next.at(-1)?.id ?? null);
    void setBreadcrumb(next);
  };

  const [mutationVersion, setMutationVersion] = useState(0);

  // Every folder in this mount is its own cache entry (the key carries currentFolderId), so
  // refetching the single mounted observer only refreshes the folder that happens to be on
  // screen. A mutation aimed at another folder (drag-and-drop onto a folder card, move/copy
  // into a sibling) would leave that folder's cached listing stale until a full reload.
  // Invalidating the whole mount scope marks every folder stale; the default
  // refetchType: "active" refetches only what is currently rendered, so the rest refresh
  // lazily on next open instead of fanning out one request per cached folder.
  const queryScopeKey = queryScope.join("\u0000");
  const signalRefresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["fileManager", ...queryScope] });
    setMutationVersion((v) => v + 1);
  }, [queryClient, queryScopeKey]);

  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [isUpdatingTags, setIsUpdatingTags] = useState(false);
  const [isUpdatingNote, setIsUpdatingNote] = useState(false);
  const [isApprovingItem, setIsApprovingItem] = useState(false);
  const [isFetchingShareLink, setIsFetchingShareLink] = useState(false);

  const makeCallbacks = (
    setLoading: (v: boolean) => void,
    outer?: FileManagerOperationCallbacks,
  ): FileManagerOperationCallbacks => ({
    onSuccess: () => {
      setLoading(false);
      signalRefresh();
      outer?.onSuccess?.();
    },
    onError: (err) => {
      setLoading(false);
      outer?.onError?.(err);
    },
  });

  const upload = (
    files: UploadFileItem[],
    targetFolderId?: string,
    cbs?: FileManagerOperationCallbacks,
  ) => {
    if (!onUpload) return;
    setIsUploading(true);
    const effectiveCtx =
      targetFolderId !== undefined ? { ...queryCtx, currentFolderId: targetFolderId } : queryCtx;
    void onUpload(files, effectiveCtx, makeCallbacks(setIsUploading, cbs));
  };

  const deleteFiles = (payload: DeleteCallbackPayload, cbs?: FileManagerOperationCallbacks) => {
    if (!onDeleteFiles) return;
    setIsDeleting(true);
    void onDeleteFiles(payload, queryCtx, makeCallbacks(setIsDeleting, cbs));
  };

  const rename = (payload: RenameCallbackPayload, cbs?: FileManagerOperationCallbacks) => {
    if (!onRename) return;
    setIsRenaming(true);
    void onRename(payload, queryCtx, makeCallbacks(setIsRenaming, cbs));
  };

  const createFolder = (
    payload: CreateFolderCallbackPayload,
    cbs?: FileManagerOperationCallbacks,
  ) => {
    if (!onCreateFolder) return;
    setIsCreatingFolder(true);
    const effectiveCtx =
      createFolderTargetFolderId !== undefined
        ? { ...queryCtx, currentFolderId: createFolderTargetFolderId }
        : queryCtx;
    void onCreateFolder(payload, effectiveCtx, makeCallbacks(setIsCreatingFolder, cbs));
  };

  const moveItems = (payload: MoveCallbackPayload, cbs?: FileManagerOperationCallbacks) => {
    if (!onMoveItems) return;
    setIsMoving(true);
    void onMoveItems(payload, queryCtx, makeCallbacks(setIsMoving, cbs));
  };

  const copyItems = (payload: MoveCallbackPayload, cbs?: FileManagerOperationCallbacks) => {
    if (!onCopyItems) return;
    setIsCopying(true);
    void onCopyItems(payload, queryCtx, makeCallbacks(setIsCopying, cbs));
  };

  const updateTags = (payload: UpdateTagsCallbackPayload, cbs?: FileManagerOperationCallbacks) => {
    if (!onUpdateTags) return;
    setIsUpdatingTags(true);
    void onUpdateTags(payload, queryCtx, makeCallbacks(setIsUpdatingTags, cbs));
  };

  const updateNote = (payload: UpdateNoteCallbackPayload, cbs?: FileManagerOperationCallbacks) => {
    if (!onUpdateNote) return;
    setIsUpdatingNote(true);
    void onUpdateNote(payload, queryCtx, makeCallbacks(setIsUpdatingNote, cbs));
  };

  const approve = (payload: ApprovalCallbackPayload, cbs?: FileManagerOperationCallbacks) => {
    if (!onApprove) return;
    setIsApprovingItem(true);
    void onApprove(payload, queryCtx, makeCallbacks(setIsApprovingItem, cbs));
  };

  const getShareLink = async (item: FileManagerItem): Promise<string> => {
    if (!onGetShareLink) throw new Error("This view cannot create share links.");
    setIsFetchingShareLink(true);
    try {
      return await onGetShareLink({ item }, queryCtx);
    } finally {
      setIsFetchingShareLink(false);
    }
  };

  const resolveDownloadUrl = useCallback(
    async (item: FileManagerItem): Promise<string | null> => {
      if (onResolveDownloadUrl) return onResolveDownloadUrl(item);
      if (item.downloadUrl) return item.downloadUrl;
      if (!item.s3Key) return null;
      return (await getS3DownloadUrl({ key: item.s3Key })).data.url;
    },
    [onResolveDownloadUrl],
  );

  const canDownload = (item: FileManagerItem): boolean =>
    item.type === "FILE" && (!!onResolveDownloadUrl || !!item.downloadUrl || !!item.s3Key);

  const isLocked = useCallback(
    (itemOrId: FileManagerItem | string | null, operation: LockedOperation): boolean => {
      if (!itemOrId || lockedFolders.length === 0) return false;
      const isItem = typeof itemOrId === "object";
      const id = isItem ? itemOrId.id : itemOrId;
      const name = isItem ? itemOrId.name : undefined;
      const isRoot = isItem ? itemOrId.parentId === null : false;
      return lockedFolders.some((locked) => {
        if (locked.rootOnly && !isRoot) return false;
        const matches =
          (locked.id !== undefined && locked.id === id) ||
          (locked.name !== undefined && locked.name === name) ||
          (locked.namePrefix !== undefined &&
            name !== undefined &&
            name.startsWith(locked.namePrefix));
        if (!matches) return false;
        if (!locked.disallow || locked.disallow.length === 0) return true;
        return locked.disallow.includes(operation);
      });
    },
    [lockedFolders],
  );

  const [selectedMap, setSelectedMap] = useState<Record<string, FileManagerItem>>({});
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const selectedItems = useMemo(() => Object.values(selectedMap), [selectedMap]);
  const selectedIds = useMemo(() => new Set(Object.keys(selectedMap)), [selectedMap]);
  const hasSelection = selectedItems.length > 0;
  const selectedCount = selectedItems.length;

  const isSelected = (id: string) => id in selectedMap;
  const selectItem = (item: FileManagerItem) =>
    setSelectedMap((prev) => ({ ...prev, [item.id]: item }));
  const deselectItem = (id: string) =>
    setSelectedMap((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  const toggleItem = (item: FileManagerItem) =>
    isSelected(item.id) ? deselectItem(item.id) : selectItem(item);
  const selectAll = (items: FileManagerItem[]) =>
    setSelectedMap(
      items.reduce<Record<string, FileManagerItem>>((acc, i) => ({ ...acc, [i.id]: i }), {}),
    );
  const deselectAll = () => setSelectedMap({});
  const toggleSelectionMode = () => setIsSelectionMode((prev) => !prev);
  const enterSelectionMode = () => setIsSelectionMode(true);
  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    deselectAll();
  };

  const peekTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isTreeVisible, setIsTreeVisible] = useState(treePeekDuration > 0);
  useEffect(() => {
    if (treePeekDuration <= 0) return;
    peekTimerRef.current = setTimeout(() => setIsTreeVisible(false), treePeekDuration);
    return () => {
      if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    };
  }, [treePeekDuration]);
  const toggleTree = useCallback(() => {
    if (peekTimerRef.current) {
      clearTimeout(peekTimerRef.current);
      peekTimerRef.current = null;
    }
    setIsTreeVisible((v) => !v);
  }, []);

  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [moveTarget, setMoveTarget] = useState<MoveOrCopyTarget | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<MoveOrCopyTarget | null>(null);
  const [tagsTarget, setTagsTarget] = useState<TagsTarget | null>(null);
  const [approvalTarget, setApprovalTarget] = useState<ApprovalTarget | null>(null);
  const [detailsTarget, setDetailsTarget] = useState<FileManagerItem | null>(null);
  const [shareTarget, setShareTarget] = useState<FileManagerItem | null>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [clipboardItems, setClipboardItems] = useState<FileManagerItem[]>([]);
  const [createFolderTargetFolderId, setCreateFolderTargetFolderId] = useState<
    string | null | undefined
  >();

  return (
    <FileManagerContext.Provider
      value={{
        currentFolderId,
        breadcrumb,
        openFolder,
        goBack,
        navigateToBreadcrumb,
        search,
        searchInputValue: innerSearch,
        onSearch,
        onClearSearch,
        items,
        folders,
        files,
        isLoading,
        isFetching,
        refetch: signalRefresh,
        upload,
        deleteFiles,
        rename,
        createFolder,
        moveItems,
        copyItems,
        updateTags,
        updateNote,
        approve,
        getShareLink,
        canGetShareLink: !!onGetShareLink,
        resolveDownloadUrl,
        canDownload,
        isUploading,
        isDeleting,
        isRenaming,
        isCreatingFolder,
        isMoving,
        isCopying,
        isUpdatingTags,
        isUpdatingNote,
        isApprovingItem,
        isFetchingShareLink,
        mutationVersion,
        permissions,
        isLocked,
      }}
    >
      <FileManagerUIContext.Provider
        value={{
          renameTarget,
          deleteTarget,
          moveTarget,
          duplicateTarget,
          tagsTarget,
          approvalTarget,
          detailsTarget,
          shareTarget,
          isUploadOpen,
          isCreateFolderOpen,
          clipboardItems,
          openRename: setRenameTarget,
          closeRename: () => setRenameTarget(null),
          openDelete: setDeleteTarget,
          closeDelete: () => setDeleteTarget(null),
          openMove: setMoveTarget,
          closeMove: () => setMoveTarget(null),
          openDuplicate: setDuplicateTarget,
          closeDuplicate: () => setDuplicateTarget(null),
          openTags: setTagsTarget,
          closeTags: () => setTagsTarget(null),
          openApproval: setApprovalTarget,
          closeApproval: () => setApprovalTarget(null),
          openDetails: setDetailsTarget,
          closeDetails: () => setDetailsTarget(null),
          openShare: setShareTarget,
          closeShare: () => setShareTarget(null),
          openUpload: () => setIsUploadOpen(true),
          closeUpload: () => setIsUploadOpen(false),
          copyToClipboard: setClipboardItems,
          clearClipboard: () => setClipboardItems([]),
          openCreateFolder: (t) => {
            setCreateFolderTargetFolderId(t);
            setIsCreateFolderOpen(true);
          },
          closeCreateFolder: () => {
            setCreateFolderTargetFolderId(undefined);
            setIsCreateFolderOpen(false);
          },
          createFolderTargetFolderId,
          selectedItems,
          selectedIds,
          hasSelection,
          selectedCount,
          isSelectionMode,
          isSelected,
          selectItem,
          deselectItem,
          toggleItem,
          selectAll,
          deselectAll,
          toggleSelectionMode,
          enterSelectionMode,
          exitSelectionMode,
          isTreeVisible,
          toggleTree,
        }}
      >
        {children}
      </FileManagerUIContext.Provider>
    </FileManagerContext.Provider>
  );
}
